import { useState } from 'react'
import { Box } from 'shared/components/Box/Box'
import { Button } from 'shared/components/Button/Button'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { Text } from 'shared/components/Text/Text'
import { Handler } from 'shared/helpers/typeHelper'
import { bgGray, darkGray, lightGray1, primaryBlack } from 'shared/theme/colors'

import { useUserProvider } from '../../../components/Context/UserContext'
import { PhoneInput } from '../../../components/MaskedInput/PhoneInput'
import { TextInput } from '../../../components/TextInput/TextInput'
import { convertMobileForAPI } from '../../../helpers/formatters'
import { useUserV3 } from '../../../hooks/query/useUserV3'
import { updateUser, User, UserPayload } from '../../../models/user'

interface ProfileContactEditProps {
  handleClose: Handler
  user: User
}

export const ProfileContactEdit = ({ handleClose, user }: ProfileContactEditProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [email, setEmail] = useState(user.email || '')
  const [mobile, setMobile] = useState(user.mobile || '')
  const [emailError, setEmailError] = useState<null | string>(null)
  const [mobileError, setMobileError] = useState<null | string>(null)
  const { showBanner, clearUserCache, permissions } = useUserProvider()
  const emailCanBeChanged = permissions.emailCanBeChanged
  const { refetch } = useUserV3(user.id, { enabled: false })

  const saveMobile = async () => {
    setIsLoading(true)
    const mobileFormatted = convertMobileForAPI(mobile)
    if (!mobileFormatted) {
      setIsLoading(false)
      setMobileError('This number is invalid')
      return
    }

    try {
      const data: UserPayload = { mobile: mobileFormatted }
      if (email !== user.email) data.email = email
      await updateUser(user.id, data)
      await Promise.all([clearUserCache(), refetch()])
      handleClose()
    } catch (err: any) {
      setIsLoading(false)
      const error = err?.response?.data || err?.data || err?.message || err || {}

      if (error.mobile) {
        const alreadyExists = error.mobile[0].includes('already exists')
        if (alreadyExists) {
          setMobileError('The phone number has been used for another account.')
          return
        }
        if (error.mobile[0]) return showBanner('error', { message: error.mobile[0] })
      }
      if (error.email) {
        const alreadyExists = error.email[0].includes('already exists')
        if (alreadyExists) {
          setEmailError('The email has been used for another account.')
          return
        }
        if (error.email[0]) return showBanner('error', { message: error.email[0] })
      }
      showBanner('error', { message: 'Something went wrong. Please try again.' })
    }
  }

  return (
    <SimpleDialog isOpen onClose={handleClose} size="small" alignText="left">
      <div style={{ width: '100%' }}>
        <Text variant="header2" fontColor={primaryBlack}>
          Edit {emailCanBeChanged ? 'email and phone' : 'mobile phone'} number
        </Text>
      </div>
      <Box mt={3} />
      {emailCanBeChanged ? (
        <TextInput
          label="E-mail"
          name="email"
          error={emailError || undefined}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          backgroundColor={bgGray}
        />
      ) : (
        <Text variant="label2" fontColor={lightGray1} align="left" mb={1}>
          E-mail:{' '}
          <Text as="span" variant="label2" fontColor={darkGray} fontWeight={600}>
            {user.email}
          </Text>
        </Text>
      )}

      <Box mt={emailError ? 5 : 3} />

      <PhoneInput
        label="Mobile Phone Number"
        name="mobile"
        type="tel"
        error={mobileError || undefined}
        value={mobile}
        onChange={(e) => setMobile(e.target.value)}
        backgroundColor={bgGray}
      />

      <Box mt={5} />
      <Button
        type="submit"
        margin="0 auto"
        onClick={saveMobile}
        variant="primary"
        size="large"
        isLoading={isLoading}
      >
        Save
      </Button>
      <Box mt={3} />
      <Button variant="text" margin="0 auto" onClick={handleClose}>
        Cancel
      </Button>
    </SimpleDialog>
  )
}
